import classNames from 'classnames';
import type { JSX, ReactNode } from 'react';
import React from 'react';

import type { IPageHeaderProps } from '@feathr/components';
import { ActionBar, PageHeader, Spinner } from '@feathr/components';
import GlobalNotifications from '@feathr/extender/components/GlobalNotifications';
import UserErrorBoundary from '@feathr/extender/components/UserErrorBoundary';
import { useActionBar } from '@feathr/extender/state';
import { useSizeAsCSSVariable } from '@feathr/hooks';

import Breadcrumbs from './Breadcrumbs';
import useScrollAndCollapse from './Page.useScrollAndCollapse';
import RouteAlert from './RouteAlert';
import usePageTitle from './usePageTitle';

import * as styles from './Page.css';

export interface IPageProps extends Omit<IPageHeaderProps, 'children' | 'theme'> {
  alerts?: ReactNode;
  BreadcrumbsComponent?: ReactNode;
  className?: string;
  children: ReactNode;
  ChipsComponent?: ReactNode;
  /** Should the header render as collapsed on page load */
  collapseHeader?: boolean;
  /** Should the header collapse upon scroll: false by default so we can opt in for specific sections */
  collapseHeaderOnScroll?: boolean;
  /** CSS className for main page content */
  contentClassName?: string;
  headerChildren?: ReactNode;
  headerTheme?: IPageHeaderProps['theme'];
  includeCurrentPageCrumb?: boolean;
  legacyScrollClassName?: string;
  /** Falls back to title if not set */
  pageTitle?: string;
  showHeader?: boolean;
  truncateTitle?: boolean;
  /**
   * Width of legacy main page content
   * @deprecated Child components should handle width
   */
  width?: 'narrow' | 'wide' | 'full';
}

function Page({
  actions,
  alerts,
  BreadcrumbsComponent,
  children,
  ChipsComponent,
  className,
  collapseHeader = true,
  collapseHeaderOnScroll = false,
  contentClassName,
  description,
  headerChildren,
  headerTheme,
  includeCurrentPageCrumb,
  legacyScrollClassName,
  loading = false,
  pageTitle,
  prefix,
  secondaryText,
  showHeader = true,
  tabs = [],
  title,
  truncateTitle,
  width,
}: Readonly<IPageProps>): JSX.Element {
  const { leftActions, rightActions } = useActionBar();
  const { shouldCollapseHeader, scrollContainerRef, scrolled } = useScrollAndCollapse({
    collapseHeaderOnScroll,
  });
  const { ref: globalNotificationsRef } = useSizeAsCSSVariable<HTMLDivElement>({
    cssVariableName: '--global-global-notifications-height',
  });
  const { ref: actionBarRef } = useSizeAsCSSVariable<HTMLDivElement>({
    cssVariableName: '--global-action-bar-height',
  });

  usePageTitle({ pageTitle, title });

  const alertItems = React.Children.map(alerts as any, (alert: JSX.Element) => {
    // Live site uses type.displayName, jest uses type.name.
    if (alert.type.displayName === 'Alert' || alert.type.name === 'Alert') {
      return React.cloneElement(alert, {
        className: classNames(styles.pageAlerts, alert.props.className),
      });
    }
    return alert;
  });

  const widthClass = width ? styles[width] : undefined;

  return (
    <div className={classNames(styles.root, className)}>
      <div
        className={classNames(styles.content, legacyScrollClassName)}
        id={'legacyScrollElement'}
        ref={scrollContainerRef}
      >
        <UserErrorBoundary>
          {showHeader && (
            <PageHeader
              actions={actions}
              BreadcrumbsComponent={
                BreadcrumbsComponent ?? (
                  <Breadcrumbs
                    includeCurrentPage={
                      includeCurrentPageCrumb ?? (shouldCollapseHeader || collapseHeader)
                    }
                  />
                )
              }
              ChipsComponent={ChipsComponent}
              collapsed={shouldCollapseHeader || collapseHeader}
              collapsible={collapseHeaderOnScroll || collapseHeader}
              description={description}
              loading={loading}
              prefix={prefix}
              RouteAlertComponent={
                <>
                  <RouteAlert className={styles.pageAlerts} />
                  {alertItems}
                </>
              }
              scrolled={scrolled}
              secondaryText={secondaryText}
              tabs={tabs}
              theme={headerTheme}
              title={title}
              truncateTitle={truncateTitle}
            >
              {headerChildren}
            </PageHeader>
          )}

          <main className={classNames(styles.main, widthClass, contentClassName)}>
            {width ? (
              loading ? (
                <div>
                  <Spinner className={styles.spinner} />
                </div>
              ) : (
                // Add wrapper div to ensure maximum compatibility for aligning page content
                <div>{children}</div>
              )
            ) : loading ? (
              <Spinner className={styles.spinner} />
            ) : (
              children
            )}
          </main>
        </UserErrorBoundary>
      </div>
      {(leftActions || rightActions) && <ActionBar left={leftActions} right={rightActions} />}
      {/* Portal for the action bar component. All instances
          of the action bar will be rendered here */}
      <div id={'react-action-bar'} ref={actionBarRef}></div>
      <header className={styles.globalNotifications} ref={globalNotificationsRef}>
        <GlobalNotifications />
      </header>
    </div>
  );
}

export default Page;
